import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Shield, ExternalLink } from 'lucide-react';
import './ListingSourceBadge.css';

/**
 * ListingSourceBadge - Elegant, minimal indicator for listing source
 * 
 * Displays a subtle badge to indicate whether a listing is:
 * - Internal (RxRooster) - Verified listing from our platform
 * - External - Aggregated from external job boards (gaswork, doccafe, locumjobs, anesthesiajobs, aana)
 * 
 * @param {string} sourceType - 'internal', 'gaswork', 'doccafe', 'locumjobs', 'anesthesiajobs', 'aana'
 * @param {boolean} verified - the recruiter has confirmed this listing on the
 *   verify page. It then earns the same "Verified" trust badge as an internal
 *   RxRooster listing, even though it stays on its external engagement path.
 * @param {boolean} orgVerified - a `verified_employer` trust badge is being
 *   rendered alongside this pill. TWO PILLS READING "VERIFIED" ON ONE CARD,
 *   MEANING DIFFERENT THINGS, is the failure this prop exists to prevent:
 *   "Verified Employer" means Rx Rooster checked the organization, while this
 *   badge's "Verified" only ever meant the listing came from Rx Rooster
 *   rather than a scraper. When the stronger claim is on screen, this one
 *   says what it actually knows.
 */
const ListingSourceBadge = ({
  sourceType = 'internal',
  verified = false,
  orgVerified = false,
}) => {
  // A recruiter-verified external listing reads as Verified (internal-grade
  // trust) without being reclassified as internal.
  const isInternal = sourceType === 'internal' || verified;

  // Map source types to display names
  const sourceDisplayNames = {
    internal: 'Verified',
    gaswork: 'GasWork',
    doccafe: 'DocCafe',
    locumjobs: 'LocumJobs',
    anesthesiajobs: 'AnesthesiaJobs',
    aana: 'AANA',
    nejm: 'NEJM',
    usajobs: 'USAJOBS',
    thecrna: 'TheCRNA',
    gasjobs: 'GasJobs',
    practicelink: 'PracticeLink',
    northstar: 'NorthStar',
    comphealth: 'CompHealth',
    practicematch: 'PracticeMatch',
    amnhealthcare: 'AMN Healthcare',
    hca: 'HCA Healthcare',
    vivian: 'Vivian Health',
    locumtenens: 'LocumTenens.com',
    ayalocums: 'Aya Locums',
    bagmask: 'BagMask',
    wellhart: 'Wellhart',
    asa: 'ASA Career Center',
    apex: 'APEX Anesthesia',
    kaiser: 'Kaiser Permanente',
    optimumperm: 'Optimum Perm',
    taleo: 'Taleo',
    'symphony-talent': 'Symphony Talent',
    mountsinai: 'Mount Sinai',
    tenet: 'Tenet Healthcare',
    christus: 'CHRISTUS Health',
    hartford: 'Hartford HealthCare',
    johnshopkins: 'Johns Hopkins',
    nyulangone: 'NYU Langone',
  };

  // The provenance claim, stated without the word "Verified", for the case
  // where the organization trust badge is already making the stronger one.
  const showsProvenanceOnly = isInternal && orgVerified;

  const tooltipText = showsProvenanceOnly
    ? 'Listed directly on Rx Rooster'
    : verified && sourceType !== 'internal'
      ? 'Confirmed by the recruiter on RxRooster'
      : isInternal
        ? 'Verified RxRooster listing'
        : `Aggregated from ${sourceDisplayNames[sourceType] || 'external job boards'}`;

  const displayText = showsProvenanceOnly
    ? 'Rx Rooster listing'
    : isInternal
      ? 'Verified'
      : 'External';

  const badge = (
    <div className={`listing-source-badge ${isInternal ? 'internal' : 'external'}`}>
      {isInternal ? (
        <Shield className="badge-icon" />
      ) : (
        <ExternalLink className="badge-icon" />
      )}
      <span className="badge-text">
        {displayText}
      </span>
    </div>
  );

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={<Tooltip id="source-badge-tooltip">{tooltipText}</Tooltip>}
    >
      {badge}
    </OverlayTrigger>
  );
};

export default ListingSourceBadge;
